<section class="ai-section">
    <div class="container">
        <div class="card">
            <div class="page-header">
                <div>
                    <h3 class="page-title">Capability models</h3>
                    <p class="page-subtitle">
                        Models for transcription (voice input), speech (voice reply) and embeddings (knowledge files).
                        The API key comes from the provider. Each capability uses its default model.
                    </p>
                </div>
                <div class="flex-spacer"></div>
                <button *ngIf="perms.has(P.capabilityCreate)" class="btn-primary btn-inline" id="cap-add" (click)="openCreate()">
                    <i class="fa-solid fa-plus"></i> Add capability model
                </button>
            </div>

            <div class="toolbar">
                <div class="form-field">
                    <label for="cap-filter">Capability</label>
                    <select id="cap-filter" [(ngModel)]="capabilityFilter" (ngModelChange)="onFilterChange()">
                        <option value="">All capabilities</option>
                        <option *ngFor="let c of capabilities" [value]="c">{{ capabilityLabels[c] }}</option>
                    </select>
                </div>
            </div>

            <div *ngIf="loadError" class="form-field"><span class="error-text" id="cap-load-error">{{ loadError }}</span></div>

            <div *ngFor="let g of groups" class="cap-group">
                <div class="page-header" style="margin-top: 14px">
                    <div>
                        <h4 class="section-title">{{ g.label }}</h4>
                        <p class="page-subtitle">
                            Default:
                            <strong *ngIf="g.defaultModel; else noDefault">{{ g.defaultModel.displayName }} ({{ g.defaultModel.modelId }})</strong>
                            <ng-template #noDefault><span class="badge off">none - this feature cannot run</span></ng-template>
                        </p>
                    </div>
                </div>
                <div class="table-wrapper table-mobile-card">
                    <table class="table" [id]="'cap-table-' + g.capability">
                        <thead>
                            <tr>
                                <th>Display name</th>
                                <th>Model ID</th>
                                <th>Provider</th>
                                <th>Options</th>
                                <th class="status-col">Default</th>
                                <th class="status-col">Enabled</th>
                                <th class="actions-wide">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngIf="!loading && g.models.length === 0">
                                <td colspan="100%">
                                    <div class="no-data"><i class="fa-solid fa-wave-square"></i> No models for this capability</div>
                                </td>
                            </tr>
                            <tr *ngFor="let m of g.models">
                                <td data-label="Display name"><strong>{{ m.displayName }}</strong></td>
                                <td data-label="Model ID" class="mono">{{ m.modelId }}</td>
                                <td data-label="Provider">{{ providerName(m) }}</td>
                                <td data-label="Options" class="mono" [title]="formatOptions(m.options)">{{ optionsSummary(m) }}</td>
                                <td data-label="Default" class="status-col">
                                    <span *ngIf="m.isDefault" class="badge ok"><i class="fa-solid fa-star"></i> Default</span>
                                    <button *ngIf="!m.isDefault && perms.has(P.capabilityUpdate)" class="btn-light btn-inline" type="button" (click)="makeDefault(m)">
                                        Make default
                                    </button>
                                </td>
                                <td data-label="Enabled" class="status-col">
                                    <span [ngClass]="m.enabled ? 'status-active' : 'status-disabled'">{{ m.enabled ? 'Yes' : 'No' }}</span>
                                </td>
                                <td data-label="Actions" class="actions-wide">
                                    <div class="actions-cell">
                                        <button *ngIf="perms.has(P.capabilityUpdate)" class="icon-btn edit" title="Edit" (click)="openEdit(m)">
                                            <i class="fa-solid fa-pen"></i>
                                        </button>
                                        <button *ngIf="perms.has(P.capabilityUpdate)" class="icon-btn view" [title]="m.enabled ? 'Disable' : 'Enable'" (click)="toggleEnabled(m)">
                                            <i class="fa-solid" [ngClass]="m.enabled ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                                        </button>
                                        <button *ngIf="perms.has(P.capabilityDelete)" class="icon-btn delete" title="Delete" (click)="remove(m)">
                                            <i class="fa-solid fa-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</section>

<div *ngIf="editorOpen" class="dialog-overlay">
    <div class="dialog-card" role="dialog" aria-modal="true">
        <div class="dialog-header">
            <div>
                <h3>{{ editingId ? 'Edit capability model' : 'Add capability model' }}</h3>
                <p>The model ID is the exact name the provider API expects (e.g. gpt-4o-transcribe).</p>
            </div>
            <button type="button" class="icon-btn back" (click)="closeEditor()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="dialog-body" (ngSubmit)="save()">
            <div class="two-col">
                <div class="form-field">
                    <label for="cap-capability">Capability</label>
                    <select id="cap-capability" name="capability" [ngModel]="form.capability" (ngModelChange)="onCapabilityChange($event)">
                        <option *ngFor="let c of capabilities" [value]="c">{{ capabilityLabels[c] }}</option>
                    </select>
                </div>
                <div class="form-field">
                    <label for="cap-provider">Provider</label>
                    <select id="cap-provider" name="providerId" [(ngModel)]="form.providerId">
                        <option value="" disabled>Select a provider</option>
                        <option *ngFor="let p of providers" [value]="p.id">{{ p.displayName }} ({{ p.code }}){{ p.hasApiKey ? '' : ' - no API key' }}</option>
                    </select>
                    <span class="hint">Only OPENAI_COMPATIBLE providers are implemented for these capabilities.</span>
                </div>
            </div>
            <div class="two-col">
                <div class="form-field">
                    <label for="cap-modelid">Model ID</label>
                    <input id="cap-modelid" name="modelId" [(ngModel)]="form.modelId" maxlength="120" placeholder="gpt-4o-mini-tts" />
                </div>
                <div class="form-field">
                    <label for="cap-name">Display name</label>
                    <input id="cap-name" name="displayName" [(ngModel)]="form.displayName" maxlength="120" placeholder="GPT-4o mini TTS" />
                </div>
            </div>

            <!-- SPEECH helpers -->
            <ng-container *ngIf="form.capability === 'SPEECH'">
                <div class="form-field">
                    <label for="cap-voices">Voices</label>
                    <p-chips inputId="cap-voices" name="voices" [ngModel]="helpers.voices" (ngModelChange)="setVoices($event)"
                        [addOnBlur]="true" separator="," [allowDuplicate]="false" placeholder="alloy, nova, shimmer" styleClass="w-full"></p-chips>
                    <span class="hint">Press Enter or type a comma after each voice. The agents pick their voice from this list.</span>
                </div>
                <div class="two-col">
                    <div class="form-field">
                        <label for="cap-default-voice">Default voice</label>
                        <select id="cap-default-voice" name="defaultVoice" [(ngModel)]="helpers.defaultVoice" (ngModelChange)="onHelpersChange()">
                            <option value="">None</option>
                            <option *ngFor="let v of helpers.voices" [value]="v">{{ v }}</option>
                        </select>
                    </div>
                    <div class="form-field">
                        <label for="cap-format">Audio format</label>
                        <select id="cap-format" name="format" [(ngModel)]="helpers.format" (ngModelChange)="onHelpersChange()">
                            <option value="">Service default (mp3)</option>
                            <option *ngFor="let f of speechFormats" [value]="f">{{ f }}</option>
                        </select>
                    </div>
                </div>
            </ng-container>

            <!-- EMBEDDING helper -->
            <div class="form-field" *ngIf="form.capability === 'EMBEDDING'">
                <label for="cap-dimensions">Dimensions</label>
                <input id="cap-dimensions" name="dimensions" type="number" min="1" step="1" [(ngModel)]="helpers.dimensions" (ngModelChange)="onHelpersChange()" />
                <span class="hint">Changing the dimensions of the default model needs a re-index of every knowledge file.</span>
            </div>

            <div class="form-field">
                <label for="cap-options">Options (JSON object)</label>
                <textarea id="cap-options" name="optionsText" rows="7" class="mono" spellcheck="false"
                    [ngModel]="form.optionsText" (ngModelChange)="onOptionsTextChange($event)"></textarea>
                <span class="hint" *ngIf="!jsonError">The fields above and this JSON stay in sync. Other keys are kept.</span>
                <span class="error-text" *ngIf="jsonError" id="cap-json-error">{{ jsonError }}</span>
            </div>

            <label class="check-row"><input type="checkbox" name="enabled" [(ngModel)]="form.enabled" /> Enabled</label>
            <label class="check-row">
                <input type="checkbox" name="isDefault" [(ngModel)]="form.isDefault" />
                Default model for {{ capabilityLabels[form.capability] }}
            </label>
            <span class="hint" *ngIf="form.isDefault && currentDefault">
                {{ currentDefault.displayName }} is the default now; it stops being the default when you save.
            </span>
            <span class="hint" *ngIf="!form.isDefault && !currentDefault">This capability has no default model; the feature cannot run without one.</span>

            <div *ngIf="formError" class="form-field"><span class="error-text" id="cap-form-error">{{ formError }}</span></div>
            <div class="dialog-footer">
                <button type="button" class="btn-light" (click)="closeEditor()">Cancel</button>
                <button type="submit" class="btn-primary" [disabled]="saving">Save</button>
            </div>
        </form>
    </div>
</div>
